import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Switch } from '@radix-ui/themes';
import { Bell } from 'lucide-react';
import { useToast } from './ToastContext';
import { apiFetch } from './api.js';
import { GlobalContext } from './GlobalContext';
import ModalTitleBar from './ModalTitleBar';

/**
 * Your own notification preferences. Currently one switch: the morning digest
 * by email. Opened from the profile menu beside Security & passkeys — personal,
 * like those, rather than a dealership setting.
 *
 * OPT-IN, and off by default. The digest's in-app audience is reports.view
 * holders, which on production is 18 of 25 users — most of the staff. Defaulting
 * this on would mail all of them every morning from the same sender that carries
 * invoices, and a daily email nobody asked for is a daily email nobody reads.
 *
 * Per user, not per dealership: this decides what lands in YOUR inbox, and one
 * manager switching it on must not sign up the rest of the team.
 *
 * GATED ON reports.view, which this file previously argued it should not be —
 * "it needs no permission beyond being signed in". That was right about who
 * DECIDES and wrong about who may RECEIVE. The digest reports the day's banked
 * payments, deal values, additional vehicle costs and overdue accounts, which is
 * why both delivery paths already demand reports.view: notificationScheduler
 * re-checks it per recipient at send time, and DigestReady carries it in
 * notificationService. Nothing leaked. What did happen is that anyone could
 * switch on a digest that would never be sent, and a control that cannot do
 * anything is worse than an absent one.
 */
export default function NotificationSettings() {
  const { showToast } = useToast();
  const { can } = useContext(GlobalContext);
  const mayReceiveDigest = can('reports.view');
  const [digestEmail, setDigestEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiFetch('/notification-preferences', { method: 'GET' })
      .then((res) => res.json())
      .then((body) => {
        if (cancelled) return;
        if (body?.ok) setDigestEmail(!!body.data?.digestEmail);
        else setError(body?.message || 'Could not load your preferences.');
      })
      .catch(() => { if (!cancelled) setError('Could not load your preferences.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async (next) => {
    //Optimistic, then reverted on failure — the switch must never be left
    //showing a setting the server did not accept.
    const previous = digestEmail;
    setDigestEmail(next);
    setSaving(true);
    try {
      const res = await apiFetch('/notification-preferences', { body: { digestEmail: next } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.message || 'Save failed.');
      setDigestEmail(!!body.data?.digestEmail);
      showToast('success', next ? 'Digest emails on.' : 'Digest emails off.');
    } catch (err) {
      setDigestEmail(previous);
      showToast('error', err.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm opacity-70">Loading…</p>;
  if (error) return <p className="text-sm" style={{ color: 'var(--theme-danger)' }}>{error}</p>;

  return (
    <div>
      <h3 className="text-base font-semibold mb-1">Notifications</h3>
      <p className="text-sm opacity-70 mb-4">
        These settings apply to you only, not to the rest of your team.
      </p>

      {mayReceiveDigest ? (
        <label className="flex items-start justify-between gap-4 py-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
          <span>
            <span className="block text-sm font-medium">Email me the morning digest</span>
            <span className="block text-xs opacity-70 mt-0.5">
              A short summary of what your team recorded the previous day, sent each
              morning. It is always available in the app under the bell; this just
              also sends it to your inbox.
            </span>
          </span>
          <Switch
            checked={digestEmail}
            disabled={saving}
            onCheckedChange={toggle}
            id="digest-email-switch"
          />
        </label>
      ) : (
        /* Said, not silently omitted. Someone who has seen a colleague's digest
           should learn why they cannot have one, and "ask for reports access"
           is something they can act on — an empty panel is not. */
        <p className="text-sm opacity-70 py-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
          The morning digest summarises the day's payments, deal values and
          overdue accounts, so it goes only to people with permission to view
          reports. Ask an administrator if you need it.
        </p>
      )}
    </div>
  );
}

/**
 * The same panel as a modal, for the profile menu.
 *
 * Shell copied from SecurityPasskeysModal deliberately — the two sit next to
 * each other in that menu, and a personal-settings dialog that looked different
 * from the personal-settings dialog beside it would read as a different kind of
 * thing.
 */
export function NotificationsModal({ isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 50,
      }}
    >
      <div
        className="modal-content"
        style={{
          background: 'var(--theme-surface)',
          borderRadius: 'var(--theme-radius-lg)',
          overflow: 'hidden',
          width: '480px',
          maxWidth: '90vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--theme-shadow-dialog)',
        }}
      >
        <ModalTitleBar icon={Bell} title="Notifications" onClose={onClose} />
        <div className="modal-body-padded" style={{ overflowY: 'auto' }}>
          <NotificationSettings />
        </div>
      </div>
    </div>,
    document.body
  );
}
